import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControlLabel,
  Switch,
  Breadcrumbs,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { addOnAPI } from '../../services/api';
import { MembershipAddOn, MemberAddOn, AddOnAssignment } from '../../types/addOn';
import AdminAccessControl from '../common/AdminAccessControl';

interface AddOnFormData {
  name: string;
  description: string;
  totalQtyAvailable: number;
  maxPerMember: number;
  setupFee: number;
  monthlyFee: number;
  status: 'active' | 'inactive';
}

const initialFormData: AddOnFormData = {
  name: '',
  description: '',
  totalQtyAvailable: 0,
  maxPerMember: 1,
  setupFee: 0,
  monthlyFee: 0,
  status: 'inactive'
};

const AddOnManagementPage: React.FC = () => {
  const [addOns, setAddOns] = useState<MembershipAddOn[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [currentAddOnId, setCurrentAddOnId] = useState<string | null>(null);
  const [formData, setFormData] = useState<AddOnFormData>(initialFormData);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [assignments, setAssignments] = useState<AddOnAssignment[]>([]);
  const [loadingAssignments, setLoadingAssignments] = useState<boolean>(false);

  // Fetch all add-ons
  const fetchAddOns = async () => {
    setLoading(true);
    try {
      const response = await addOnAPI.getAll();
      setAddOns(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching add-ons:', err);
      setError('Failed to load add-ons. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAddOns();
  }, []);

  // Handle form field changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData({
        ...formData,
        [name]: checked
      });
    } else if (type === 'number') {
      setFormData({
        ...formData,
        [name]: value === '' ? 0 : Number(value)
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  // Open dialog for creating a new add-on
  const handleOpenCreateDialog = () => {
    setFormData(initialFormData);
    setEditMode(false);
    setCurrentAddOnId(null);
    setDialogOpen(true);
  };

  // Open dialog for editing an existing add-on
  const handleOpenEditDialog = async (addOn: MembershipAddOn) => {
    setFormData({
      name: addOn.name,
      description: addOn.description || '',
      totalQtyAvailable: addOn.totalQtyAvailable,
      maxPerMember: addOn.maxPerMember,
      setupFee: addOn.setupFee,
      monthlyFee: addOn.monthlyFee,
      status: addOn.status
    });
    setEditMode(true);
    setCurrentAddOnId(addOn.id);
    setDialogOpen(true);

    // Fetch assignments if totalQtyAvailable > 0
    if (addOn.totalQtyAvailable > 0) {
      await fetchAssignments(addOn.id);
    } else {
      setAssignments([]);
    }
  };

  // Fetch member assignments for an add-on
  const fetchAssignments = async (addOnId: string) => {
    setLoadingAssignments(true);
    try {
      const response = await addOnAPI.getAssignments(addOnId);
      setAssignments(response.data);
    } catch (err) {
      console.error('Error fetching assignments:', err);
      setAssignments([]);
    } finally {
      setLoadingAssignments(false);
    }
  };

  // Close dialog
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setDeleteDialogOpen(false);
    setAssignments([]);
  };

  // Handle submit for creating or updating an add-on
  const handleSubmit = async () => {
    try {
      // Validate form
      if (!formData.name.trim()) {
        setError('Add-on name is required');
        return;
      }

      if (formData.monthlyFee < 0 || formData.setupFee < 0) {
        setError('Fees cannot be negative');
        return;
      }

      if (formData.totalQtyAvailable < 0 || formData.maxPerMember < 1) {
        setError('Invalid quantity values');
        return;
      }

      if (editMode && currentAddOnId) {
        // Update existing add-on
        await addOnAPI.update(currentAddOnId, formData);
        setSuccess('Add-on updated successfully!');
      } else {
        // Create new add-on
        await addOnAPI.create(formData);
        setSuccess('Add-on created successfully!');
      }

      // Close dialog and refresh data
      setDialogOpen(false);
      fetchAddOns();

      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 5000);
    } catch (err: any) {
      console.error('Error saving add-on:', err);
      setError(err.response?.data?.message || 'Failed to save add-on. Please try again.');
    }
  };

  // Open delete confirmation dialog
  const handleOpenDeleteDialog = (id: string) => {
    setCurrentAddOnId(id);
    setDeleteDialogOpen(true);
  };

  // Handle delete
  const handleDelete = async () => {
    if (!currentAddOnId) return;

    try {
      await addOnAPI.delete(currentAddOnId);
      setSuccess('Add-on deleted successfully!');
      setDeleteDialogOpen(false);
      fetchAddOns();

      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 5000);
    } catch (err: any) {
      console.error('Error deleting add-on:', err);
      setError(err.response?.data?.message || 'Failed to delete add-on. Please try again.');
      setDeleteDialogOpen(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <AdminAccessControl>
        {/* Breadcrumbs */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          sx={{ mb: 3 }}
        >
          <RouterLink
            style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
            to="/dashboard"
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            Dashboard
          </RouterLink>
          <Typography color="text.primary">Membership Add-Ons</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">
            Membership Add-Ons
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
          >
            Create Add-On
          </Button>
        </Box>

        {/* Error/Success Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Add-Ons Table */}
        <Paper sx={{ p: 2 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Setup Fee</TableCell>
                    <TableCell align="right">Monthly Fee</TableCell>
                    <TableCell align="center">Qty Available</TableCell>
                    <TableCell align="center">Max Per Member</TableCell>
                    <TableCell align="center">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {addOns.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No add-ons found. Create one to get started.
                      </TableCell>
                    </TableRow>
                  ) : (
                    addOns.map((addOn) => (
                      <TableRow 
                        key={addOn.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleOpenEditDialog(addOn)}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold" color="primary">
                            {addOn.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                            {addOn.description || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          ${addOn.setupFee}
                        </TableCell>
                        <TableCell align="right">
                          ${addOn.monthlyFee}/mo
                        </TableCell>
                        <TableCell align="center">
                          {addOn.totalQtyAvailable === 0 ? 'Unlimited' : addOn.totalQtyAvailable}
                        </TableCell>
                        <TableCell align="center">
                          {addOn.maxPerMember}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={addOn.status}
                            color={addOn.status === 'active' ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editMode ? 'Edit Add-On' : 'Create New Add-On'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                fullWidth
              />

              <TextField
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                multiline
                rows={3}
                fullWidth
              />

              <TextField
                label="Setup Fee"
                name="setupFee"
                type="number"
                value={formData.setupFee}
                onChange={handleChange}
                inputProps={{ min: 0, step: 0.01 }}
                fullWidth
                helperText="One-time fee charged when member subscribes"
              />

              <TextField
                label="Monthly Fee"
                name="monthlyFee"
                type="number"
                value={formData.monthlyFee}
                onChange={handleChange}
                required
                inputProps={{ min: 0, step: 0.01 }}
                fullWidth
                helperText="Recurring monthly charge"
              />

              <TextField
                label="Total Quantity Available"
                name="totalQtyAvailable"
                type="number"
                value={formData.totalQtyAvailable}
                onChange={handleChange}
                inputProps={{ min: 0 }}
                fullWidth
                helperText="0 = unlimited availability"
              />

              <TextField
                label="Max Per Member"
                name="maxPerMember"
                type="number"
                value={formData.maxPerMember}
                onChange={handleChange}
                inputProps={{ min: 1 }}
                fullWidth
                helperText="Maximum number one member can subscribe to"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.status === 'active'}
                    onChange={(e) =>
                      setFormData({ ...formData, status: e.target.checked ? 'active' : 'inactive' })
                    }
                    name="status"
                  />
                }
                label="Active"
              />

              {/* Show assignments info if totalQtyAvailable > 0 and in edit mode */}
              {editMode && formData.totalQtyAvailable > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Assigned: {assignments.length} / {formData.totalQtyAvailable} units
                  </Typography>

                  {/* Assignments accordion */}
                  <Accordion sx={{ mt: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle2">Assignments</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      {loadingAssignments ? (
                        <Box display="flex" justifyContent="center" p={2}>
                          <CircularProgress size={24} />
                        </Box>
                      ) : assignments.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No members currently assigned to this add-on.
                        </Typography>
                      ) : (
                        <List dense>
                          {assignments.map((assignment, index) => (
                            <React.Fragment key={assignment.id}>
                              {index > 0 && <Divider />}
                              <ListItem>
                                <ListItemText
                                  primary={assignment.memberName}
                                  secondary={`${assignment.memberEmail} • Since: ${new Date(assignment.startDate).toLocaleDateString()}`}
                                />
                              </ListItem>
                            </React.Fragment>
                          ))}
                        </List>
                      )}
                    </AccordionDetails>
                  </Accordion>
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
            {editMode && (
              <Button 
                onClick={() => handleOpenDeleteDialog(currentAddOnId!)} 
                color="error"
                variant="outlined"
              >
                Delete
              </Button>
            )}
            <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
              <Button onClick={handleCloseDialog}>Cancel</Button>
              <Button onClick={handleSubmit} variant="contained" color="primary">
                {editMode ? 'Update' : 'Create'}
              </Button>
            </Box>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={handleCloseDialog}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this add-on? This action cannot be undone.
              Add-ons with active subscriptions cannot be deleted.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleDelete} variant="contained" color="error">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </AdminAccessControl>
    </Container>
  );
};

export default AddOnManagementPage;
